import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { InnerSearch } from "../components/search/InnerSearch";
import { RealtedVideo } from "../components/realtedVideo/RealtedVideo";
import { AskedQuestions } from "../help_support/components/AskedQuestions";
import { HelpfulFeedback } from "../components/HelpfulFeedback/HelpfulFeedback";
import { Paginator } from "primereact/paginator";

export const FaqDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [first, setFirst] = useState(0);
  const rows = 5;
  const [totalRecords, setTotalRecords] = useState(0);
  const apiBaseURL = process.env.REACT_APP_API_URL || window.location.origin;
  const fetchIDsUsed = state?.detailsIDs;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [faqListData, setFaqListData] = useState([]);
  const [faqDetailsData, setFaqDetailsData] = useState([]);

  const innerSearch = useMemo(
    () => ({
      pageName: "FAQ’s",
      breadcrumb: [
        {
          label: "Home",
          url: "/VZYSupport/helpSupport",
        },
        { label: fetchIDsUsed?.contentTitle || "Faq Details" },
      ],
    }),
    [fetchIDsUsed?.contentTitle],
  );

  const FetchFAQDetails = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = {
        ContentCode: fetchIDsUsed?.contentCode || "",
        ContentTypeID: fetchIDsUsed?.contentTypeID || 0,
        CategoryID: 0,
        IsActive: 1,
        PageNumber: 1,
        PageSize: 10,
        SearchValue: fetchIDsUsed?.contentCode || "",
        IsSearch: fetchIDsUsed?.IsSearch || 0,
      };
      const response = await axios.post(
        `${apiBaseURL}/api/VZYHelpSupport/FetchContentSteps`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      const result = response?.data?.result;
      if (!result || (Array.isArray(result) && result.length === 0)) {
        navigate("/VZYSupport/NoResultsFound");
        return;
      }
      setFaqDetailsData(result?.contentSteps ?? []);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("Failed to load FAQ details.");
    } finally {
      setLoading(false);
    }
  }, [
    fetchIDsUsed?.contentCode,
    fetchIDsUsed?.contentTypeID,
    fetchIDsUsed?.IsSearch,
    apiBaseURL,
    navigate,
  ]);

  useEffect(() => {
    FetchFAQDetails();
  }, [FetchFAQDetails]);

  const faqItem = faqDetailsData?.[0];

  const fetchFAQList = useCallback(
    async (pageNumber = 1) => {
      setError("");

      try {
        const payload = {
          contentCode: "",
          contentTypeID: 1,
          categoryID: 0,
          isActive: 1,
          pageNumber,
          pageSize: rows,
          searchValue: "",
          CategoryIDs: [],
        };

        const response = await axios.post(
          `${apiBaseURL}/api/VZYHelpSupport/FetchFAQ`,
          payload,
          { headers: { "Content-Type": "application/json" } },
        );

        const result = response?.data?.result;

        setFaqListData(result?.fAQs ?? []);
        setTotalRecords(result?.totalRecords ?? 0);
      } catch {
        setError("Failed to load FAQs.");
      }
    },
    [apiBaseURL],
  );

  useEffect(() => {
    fetchFAQList(1);
  }, [fetchFAQList]);

  const getEmbedUrl = (url = "") => {
    if (!url) return "";

    try {
      if (url.includes("youtube.com/watch")) {
        const videoId = new URL(url).searchParams.get("v");
        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
      }

      if (url.includes("youtu.be")) {
        const videoId = url.split("/").pop();
        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
      }

      return url;
    } catch {
      return "";
    }
  };

  const renderMedia = () => {
    if (!faqItem?.imageAndVideoUrl) return null;

    if (faqItem.imageAndVideoType === 2) {
      return (
        <iframe
          src={getEmbedUrl(faqItem.imageAndVideoUrl)}
          style={{
            width: "100%",
            minHeight: "480px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="FAQ Video"
        />
      );
    }

    if (faqItem.imageAndVideoType === 1) {
      return (
        <img
          src={faqItem.imageAndVideoUrl}
          alt="FAQ"
          style={{
            width: "100%",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        />
      );
    }

    return null;
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    fetchFAQList(e.page + 1);
  };

  return (
    <>
      <InnerSearch innerSearch={innerSearch} />

      <section className="inner-page-details">
        <div className="container">
          {loading && <p>Loading...</p>}
          {error && <p className="error-text">{error}</p>}

          {!loading && !error && (
            <div className="row">
              <div className="col-md-9">
                <h4 className="detail-page-title">{faqItem?.contentTitle}</h4>

                <div className="faq-desciption-row">
                  {faqItem?.contentSteps && (
                    <div
                      className="faq-html-content"
                      dangerouslySetInnerHTML={{
                        __html: faqItem.contentSteps,
                      }}
                    />
                  )}

                  {renderMedia()}
                </div>

                <HelpfulFeedback
                  fetchIDsUsed={faqItem}
                  title="Was this Answer helpful ?"
                />

                <h4 className="detail-page-title">Related FAQ’s</h4>
                <div className="faq-accordian">
                  <AskedQuestions faqListData={faqListData} />

                  {totalRecords > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={totalRecords}
                      onPageChange={onPageChange}
                      className="mt-3"
                    />
                  )}
                </div>
              </div>

              <div className="col-md-3">
                <h4 className="detail-page-sidebar-title">Related Videos</h4>
                <div>
                  <RealtedVideo />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
};
